import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store/store';
import { fetchFreshdeskApiKey, clearApiKey } from '../store/slices/freshdeskSlice';
import { addNotification } from '../store/slices/notificationSlice';
import freshdeskIllustration from '../assets/img/Illustration-Freshdesk.png';
import BackButton from '../components/BackButton';
import { useTranslation } from 'react-i18next';
import UpgradePromoBlock from '../components/UpgradePromoBlock';
import { useLicenseCheck } from '../hooks/useLicenseCheck';
import HowItWorks from '../components/license-buy/HowItWorks';
import LicenseBuyCards from '../components/license-buy/LicenseBuyCards';
import FAQ from '../components/license-buy/FAQ';
import WorkExample from '../components/license-buy/WorkExample';
import GetNow from '../components/license-buy/GetNow';
import ContactForm from '../components/license-buy/ContactForm';
import { fetchProducts } from '../store/slices/licenseSlice';

const FreshdeskPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { apiKey, loading, error } = useSelector((state: RootState) => state.freshdesk);
  const { t } = useTranslation();
  const hasPaidLicense = useLicenseCheck('freshdesk');
  const profile = useSelector((state: RootState) => state.profile);

  useEffect(() => {
    dispatch(clearApiKey());
    dispatch(fetchFreshdeskApiKey());
  }, [dispatch]);

  const handleCopyApiKey = async () => {
    if (!apiKey) return;

    try {
      await navigator.clipboard.writeText(apiKey);
      dispatch(addNotification({
        type: 'success',
        title: t('notification_success'),
        message: t('freshdesk_copy_success'),
      }));
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        title: t('notification_error'),
        message: t('freshdesk_copy_error'),
      }));
    }
  };

  return (
    <div>
      <BackButton />

      {profile && !profile.contact?.parentUserId && !hasPaidLicense && (
        <UpgradePromoBlock pluginName="freshdesk" />
      )}
      
      <div className="freshdesk-content">
        <div className="center">
          <h1 className="freshdesk-content-title">{t('freshdesk_api_key_title')}</h1>
          <p className="freshdesk-content-description">
            <span>{t('freshdesk_api_key_description')}</span>
            <br/>
            <span>{t('freshdesk_integration_manual')} <a target="_blank" href="#">{t('freshdesk_manual_link')}</a></span>
          </p>
        </div>
        <div className="freshdesk-illustration">
          <img src={freshdeskIllustration} alt={t('freshdesk_api_key_title')} />
        </div>
        <div className="form-row">
          <button 
            onClick={handleCopyApiKey}
            className="button button--primary"
            disabled={!apiKey || loading}
          >
            {loading ? t('form_loading') : t('copyAPIKey')}
          </button>
        </div>
      </div>

      <HowItWorks licenseType={"freshdesk"} />
      
      <LicenseBuyCards
        licenseType={"freshdesk"}
      />

      <FAQ licenseType={"freshdesk"} />

      <WorkExample licenseType={"freshdesk"} />

      <GetNow licenseType={"freshdesk"} />

      <ContactForm licenseType={"freshdesk"} />

    </div>
  );
};

export default FreshdeskPage; 